<script setup lang="ts">
import CollapsibleSearchPanel from '@/components/Globals/CollapsibleSearchPanel.vue'
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue'
import NewDateRangePicker from '@/components/Globals/Inputs/NewDateRangePicker.vue'
import SearchBarField from '@/components/Globals/SearchBarField.vue'
import IcAnimateSpin from '@/components/Icons/IcAnimateSpin.vue'
import IcSearch from '@/components/Icons/IcSearch.vue'
import { useReportSearchOrchestration } from '@/composables/useReportSearchOrchestration'
import { useI18n } from '@/composable/useI18n'
import type { IReportSearchBarSchema } from '@/interfaces/api/schemas/report'
import {
  compareSearchFieldsSchema,
  reportSearchPanelSchema,
} from '@/schemas/ReportSearchPanelSchema'
import { useCompanyStore } from '@/store/company'
import { useGroupStore } from '@/store/group'
import { useReportStore } from '@/store/report'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { Field, Form } from 'vee-validate'
import { computed, onMounted, ref, watch } from 'vue'
import { ValidationError } from 'yup'

interface IReportSearchPanelFormValues extends IReportSearchBarSchema {
  compareGroup?: IReportSearchBarSchema['group']
  comparePeriod?: IReportSearchBarSchema['period']
  compareShift?: IReportSearchBarSchema['shift']
}

const { t } = useI18n()
const { executeSearch, clearCompare, isLoading } = useReportSearchOrchestration()

const formRef = ref<InstanceType<typeof Form>>()
const isCompareEnabled = ref(false)

const { listGroups } = storeToRefs(useGroupStore())
const reportStore = useReportStore()
const {
  getBasePeriodFilter,
  getBaseGroupFilter,
  getBaseChartColors,
  getComparePeriodFilter,
  getCompareGroupFilter,
  getCompareChartColors,
  showAlarmReportBase,
  showAlarmReportCompare,
  compareReport,
  compareGroup,
} = storeToRefs(reportStore)
const { setBaseChartColor, setCompareChartColor } = reportStore

const { fetchCompanyShifts } = useCompanyStore()
const { shifts, currentCompany, hasShifts } = storeToRefs(useCompanyStore())

const panelControlClass =
  '[&_input]:rounded-none [&_button]:rounded-none [&_select]:rounded-none'

const baseGridClass = computed(() =>
  hasShifts.value
    ? 'lg:grid-cols-[auto_minmax(0,1fr)_minmax(0,1.2fr)_auto_auto]'
    : 'lg:grid-cols-[auto_minmax(0,1fr)_minmax(0,1.2fr)_auto]',
)

const compareGridClass = computed(() =>
  hasShifts.value
    ? 'lg:grid-cols-[auto_minmax(0,1fr)_minmax(0,1.2fr)_auto_auto_auto]'
    : 'lg:grid-cols-[auto_minmax(0,1fr)_minmax(0,1.2fr)_auto_auto]',
)

const initialValues = computed(() => ({
  group: getBaseGroupFilter.value?.id ? getBaseGroupFilter.value : undefined,
  period: getBasePeriodFilter.value?.length ? getBasePeriodFilter.value : undefined,
  shift: reportStore.baseShift,
  compareGroup: getCompareGroupFilter.value?.id ? getCompareGroupFilter.value : undefined,
  comparePeriod: getComparePeriodFilter.value?.length ? getComparePeriodFilter.value : undefined,
  compareShift: reportStore.compareShift,
}))

function hasComparePersistedState(): boolean {
  if (compareGroup.value?.id) return true
  const report = compareReport.value
  if (!report) return false
  return (
    (report.energy_sensors_readings?.length ?? 0) > 0 ||
    (report.temperature_sensors_readings?.length ?? 0) > 0 ||
    (report.door_open_readings?.length ?? 0) > 0
  )
}

function handleBaseColorChange(event: Event) {
  setBaseChartColor((event.target as HTMLInputElement).value)
}

function handleCompareColorChange(event: Event) {
  setCompareChartColor((event.target as HTMLInputElement).value)
}

function enableComparison() {
  isCompareEnabled.value = true
}

function clearCompareFieldErrors() {
  formRef.value?.setFieldError('compareGroup', undefined)
  formRef.value?.setFieldError('comparePeriod', undefined)
  formRef.value?.setFieldError('compareShift', undefined)
}

function handleRemoveComparison() {
  isCompareEnabled.value = false
  clearCompare()
  formRef.value?.setFieldValue('compareGroup', undefined)
  formRef.value?.setFieldValue('comparePeriod', undefined)
  formRef.value?.setFieldValue('compareShift', null)
  clearCompareFieldErrors()
}

async function onSubmit(values: object) {
  const castedValues = values as IReportSearchPanelFormValues

  if (isCompareEnabled.value) {
    try {
      await compareSearchFieldsSchema.validate(
        {
          compareGroup: castedValues.compareGroup,
          comparePeriod: castedValues.comparePeriod,
        },
        { abortEarly: false },
      )
    } catch (error) {
      if (error instanceof ValidationError) {
        error.inner.forEach((issue) => {
          if (issue.path) {
            formRef.value?.setFieldError(issue.path, issue.message)
          }
        })
        return
      }
      throw error
    }
  }

  await executeSearch({
    base: {
      group: castedValues.group,
      period: castedValues.period,
      shift: castedValues.shift ?? null,
    },
    compare: isCompareEnabled.value
      ? {
          group: castedValues.compareGroup!,
          period: castedValues.comparePeriod!,
          shift: castedValues.compareShift ?? null,
        }
      : undefined,
    isCompareEnabled: isCompareEnabled.value,
  })
}

onMounted(() => {
  fetchCompanyShifts()
  isCompareEnabled.value = hasComparePersistedState()
})

watch(currentCompany, (newValue) => {
  if (newValue) {
    fetchCompanyShifts()
  }
})
</script>

<template>
  <CollapsibleSearchPanel :title="t('report.panel.title')" class="mt-6">
    <Form
      ref="formRef"
      :validation-schema="reportSearchPanelSchema"
      :initial-values="initialValues"
      class="flex w-full min-w-0 flex-col p-4"
      :class="panelControlClass"
      @submit="onSubmit">
      <div
        class="grid w-full min-w-0 grid-cols-1 items-start gap-3"
        :class="baseGridClass">
        <SearchBarField :label="t('report.panel.fields.color')" class="w-auto shrink-0">
          <div class="flex h-[46px] items-center">
            <input
              type="color"
              :value="getBaseChartColors"
              class="h-10 w-10 cursor-pointer rounded-none border border-stroke p-0.5"
              @input="handleBaseColorChange" />
          </div>
        </SearchBarField>

        <SearchBarField :label="t('report.panel.fields.group')" class="min-w-0">
          <Dropdown
            name="group"
            :options="listGroups"
            label-attribute="name"
            :already-selected-option="getBaseGroupFilter" />
        </SearchBarField>

        <SearchBarField :label="t('report.panel.fields.period')" class="min-w-0">
          <Field name="period" v-slot="{ field }">
            <NewDateRangePicker
              v-bind="field"
              :already-selected-period="getBasePeriodFilter"
              :placeholder="t('plugins.vue_datepicker.placeholder')"
              class="w-full rounded-none" />
          </Field>
        </SearchBarField>

        <SearchBarField
          v-if="hasShifts"
          :label="t('dashboard.header.buttons.shift')"
          class="min-w-0">
          <Dropdown
            name="shift"
            :options="shifts"
            :placeholder="t('placeholders.select_shift')"
            label-attribute="name"
            :has-all-option="true" />
        </SearchBarField>

        <div class="flex flex-col">
          <span
            class="invisible inline-flex self-start px-2 py-1 text-xs font-medium"
            aria-hidden="true">
            {{ t('report.panel.fields.alarm_analysis') }}
          </span>
          <div class="flex min-h-[46px] items-center">
            <label class="flex cursor-pointer select-none items-center gap-2">
              <input
                v-model="showAlarmReportBase"
                type="checkbox"
                class="h-4 w-4 rounded-none border-stroke text-blue focus:ring-0" />
              <span class="text-sm font-medium text-black">{{ t('report.panel.fields.alarm_analysis') }}</span>
            </label>
          </div>
        </div>
      </div>

      <div v-if="!isCompareEnabled" class="mt-3">
        <button
          type="button"
          class="rounded-none border border-transparent px-3 py-2 text-sm font-medium text-blue hover:bg-blue-50"
          @click="enableComparison">
          {{ t('report.panel.buttons.add_comparison') }}
        </button>
      </div>

      <div
        v-else
        class="mt-6 grid w-full min-w-0 grid-cols-1 items-start gap-3 border-t border-gray-100 pt-6"
        :class="compareGridClass">
        <SearchBarField :label="t('report.panel.fields.color')" class="w-auto shrink-0">
          <div class="flex h-[46px] items-center">
            <input
              type="color"
              :value="getCompareChartColors"
              class="h-10 w-10 cursor-pointer rounded-none border border-stroke p-0.5"
              @input="handleCompareColorChange" />
          </div>
        </SearchBarField>

        <SearchBarField :label="t('report.panel.fields.group')" class="min-w-0">
          <Dropdown
            name="compareGroup"
            :options="listGroups"
            label-attribute="name"
            :already-selected-option="getCompareGroupFilter" />
        </SearchBarField>

        <SearchBarField :label="t('report.panel.fields.period')" class="min-w-0">
          <Field name="comparePeriod" v-slot="{ field }">
            <NewDateRangePicker
              v-bind="field"
              :already-selected-period="getComparePeriodFilter"
              :placeholder="t('plugins.vue_datepicker.placeholder')"
              class="w-full rounded-none" />
          </Field>
        </SearchBarField>

        <SearchBarField
          v-if="hasShifts"
          :label="t('dashboard.header.buttons.shift')"
          class="min-w-0">
          <Dropdown
            name="compareShift"
            :options="shifts"
            :placeholder="t('placeholders.select_shift')"
            label-attribute="name"
            :has-all-option="true" />
        </SearchBarField>

        <div class="flex flex-col">
          <span
            class="invisible inline-flex self-start px-2 py-1 text-xs font-medium"
            aria-hidden="true">
            {{ t('report.panel.fields.alarm_analysis') }}
          </span>
          <div class="flex min-h-[46px] items-center">
            <label class="flex cursor-pointer select-none items-center gap-2">
              <input
                v-model="showAlarmReportCompare"
                type="checkbox"
                class="h-4 w-4 rounded-none border-stroke text-blue focus:ring-0" />
              <span class="text-sm font-medium text-black">{{ t('report.panel.fields.alarm_analysis') }}</span>
            </label>
          </div>
        </div>

        <div class="flex flex-col">
          <span
            class="invisible inline-flex self-start px-2 py-1 text-xs font-medium"
            aria-hidden="true">
            {{ t('report.panel.buttons.remove_comparison') }}
          </span>
          <div class="flex min-h-[46px] items-center">
            <button
              type="button"
              class="flex items-center gap-1 rounded-none border border-transparent px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              @click="handleRemoveComparison">
              <XMarkIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
              <span class="hidden sm:inline">{{ t('report.panel.buttons.remove_comparison') }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="mt-4 flex w-full min-w-0 justify-end border-t border-stroke pt-4">
        <button
          type="submit"
          :disabled="isLoading"
          class="flex w-full flex-row items-center justify-center gap-2 rounded-none border border-transparent bg-blue px-4 py-3 text-sm font-semibold text-white hover:bg-blue-2 sm:w-auto">
          <IcSearch v-if="!isLoading" class="h-5 w-5 shrink-0" />
          <span v-else class="animate-spin">
            <IcAnimateSpin class="h-5 w-5 text-white" />
          </span>
          {{ t('report.panel.buttons.search') }}
        </button>
      </div>
    </Form>
  </CollapsibleSearchPanel>
</template>
